import { Component, Input } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { RegexPattern, validateGLNCheckDigit, validateVatId } from '@markant-platform/shared/util-helpers';
import { MarkantElementsModule, MarkantSelectOption } from '@markant/elements-angular';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControlErrorPipe,
  FormControlIsRequiredPipe,
  FormControlKeyErrorPipe,
} from '@markant-platform/shared/util-pipes';
import { NgIf } from '@angular/common';
import { OrganizationFurtherDataFormGroup } from '@markant-platform/registration/model';
import { Industry } from 'data-model';

@Component({
  standalone: true,
  selector: 'markant-platform-registration-organization-further-data',
  templateUrl: './registration-organization-further-data.component.html',
  styleUrls: ['../registration.scss'],
  imports: [
    ReactiveFormsModule,
    MarkantElementsModule,
    TranslocoPipe,
    FormControlKeyErrorPipe,
    FormControlErrorPipe,
    NgIf,
    FormControlIsRequiredPipe,
  ],
})
export class RegistrationOrganizationFurtherDataComponent {
  @Input() organizationForm!: OrganizationFurtherDataFormGroup;
  @Input() showError?: boolean;
  @Input() isGlnRequiredCampaign?: boolean;

  pathToTranslation = 'organization.details.fields.';
  industryOptions = this.setIndustryOptions();

  constructor(private readonly translocoService: TranslocoService) {
    this.translocoService.langChanges$
      .pipe(takeUntilDestroyed())
      .subscribe(() => (this.industryOptions = this.setIndustryOptions()));
  }

  private setIndustryOptions(): MarkantSelectOption[] {
    return Object.values(Industry).map(industry => ({
      value: industry,
      representation: this.translocoService.translate('organization.details.fields.industry.' + industry),
    })) as MarkantSelectOption[];
  }

  changeGlnRequired($event: boolean): void {
    const label = document.getElementById('gln')?.getElementsByTagName('label')?.item(0);
    if (!$event) {
      this.organizationForm.controls.gln.enable();
      this.organizationForm.controls.gln.addValidators([
        Validators.required,
        Validators.pattern(RegexPattern.GLN_PATTERN),
        validateGLNCheckDigit,
      ]);
      this.organizationForm.controls.gln.setErrors({ required: true });
      this.organizationForm.controls.gln.markAsUntouched();
    } else {
      this.organizationForm.controls.gln.disable();
      this.organizationForm.controls.gln.setValue('');
      this.organizationForm.controls.gln.removeValidators([
        Validators.required,
        Validators.pattern(RegexPattern.GLN_PATTERN),
        validateGLNCheckDigit,
      ]);
      label?.classList.remove('mdc-text-field--invalid');
    }
  }

  changeTaxNumberRequired($event: boolean): void {
    const label = document.getElementById('salesTaxId')?.getElementsByTagName('label')?.item(0);
    if (!$event) {
      this.organizationForm.controls.taxNumber.setValue('');
      this.organizationForm.controls.taxNumber.disable();
      this.organizationForm.controls.taxNumber.removeValidators([Validators.required]);
      this.organizationForm.controls.salesTaxId.enable();
      this.organizationForm.controls.salesTaxId.addValidators([Validators.required, validateVatId]);
      this.organizationForm.controls.salesTaxId.markAsUntouched();
      if (this.showError) {
        this.organizationForm.controls.salesTaxId.setErrors({ required: true });
        label?.classList.add('mdc-text-field--invalid');
      }
    } else {
      this.organizationForm.controls.taxNumber.enable();
      this.organizationForm.controls.taxNumber.addValidators([Validators.required]);
      this.organizationForm.controls.salesTaxId.setValue('');
      this.organizationForm.controls.salesTaxId.removeValidators([Validators.required, validateVatId]);
      this.organizationForm.controls.salesTaxId.disable();
      label?.classList.remove('mdc-text-field--invalid');
    }
  }
}
